import { Button } from "@/components/ui/button";
import { Calculator, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { RUNNING, familyOf, fmtEur, fmtGrams, fmtMinutes, positionTotals } from "@/hooks/useKalkulation";

// Zeile in der Dateiablage: Button „Kalkulieren“ + Status/Ergebnis der letzten Kalkulation.
export function KalkulationFileRow({ att, calc }) {
  if (!calc || !calc.allowedExt.includes((att.ext || "").toLowerCase())) return null;
  const job = calc.jobs[familyOf(att)];
  const running = job && RUNNING.includes(job.status);
  const disabledReason = calc.locked ? "Preise sind nach der Preisfreigabe gesperrt – Kalkulieren nicht möglich." : running ? "Kalkulation läuft…" : "";
  return (
    <div className="flex items-center gap-2 flex-wrap pt-1" data-testid={`kalk-row-${att.id}`}>
      {calc.canCalc && (
        <span title={disabledReason || "Preis über den Website-Kalkulator berechnen"} data-testid={`kalk-button-wrap-${att.id}`}>
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!disabledReason} onClick={() => calc.onOpen(att)} data-testid={`kalk-button-${att.id}`}>
            <Calculator className="w-3.5 h-3.5 mr-1" /> Kalkulieren
          </Button>
        </span>
      )}
      <KalkJobStatus job={job} attId={att.id} position={(calc.positions || []).find((p) => p.kalk?.file_family === familyOf(att))} />
    </div>
  );
}

function KalkJobStatus({ job, attId, position }) {
  if (!job) return null;
  if (RUNNING.includes(job.status)) {
    return <span className="text-xs text-blue-700 inline-flex items-center gap-1" data-testid={`kalk-status-${attId}`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Kalkuliere…</span>;
  }
  if (job.status === "fehler") {
    return <span className="text-xs text-rose-700 inline-flex items-start gap-1" data-testid={`kalk-error-${attId}`}><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {job.message}</span>;
  }
  const r = job.result || {};
  // Preis = tatsächlich berechneter Betrag der Position (wie in der Positionstabelle); Fallback: Ergebnis der Website
  const t = position ? positionTotals(position) : { brutto: r.price_gross, netto: r.price_net };
  const ang = position?.kalk?.preis_modus === "stueck" ? position.kalk.angebot : null;
  const rundung = ang ? Math.round((ang.gesamt_brutto - ang.kalkuliert_brutto) * 100) / 100 : 0;
  return (
    <span className="text-xs text-slate-700 inline-flex items-center gap-1 flex-wrap" data-testid={`kalk-result-${attId}`}>
      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
      <b className="font-mono" data-testid={`kalk-result-brutto-${attId}`}>{fmtEur(t.brutto)}</b> brutto · <span className="font-mono">{fmtEur(t.netto)}</span> netto · {fmtGrams(r.material_grams)} · {fmtMinutes(r.print_time_minutes)}
      {r.estimated && <span className="ml-1 px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200" title="Kein exaktes Slicer-Ergebnis – Preis bitte prüfen" data-testid={`kalk-estimated-${attId}`}>Schätzwert</span>}
      {ang && position && (
        <span className="basis-full pl-[18px] text-slate-600" data-testid={`kalk-angebot-${attId}`}>
          Im Angebot: <span className="font-mono">{position.menge} × {fmtEur(position.einzelpreis_brutto)} = {fmtEur(t.brutto)}</span> brutto
          {rundung !== 0 && <span className="text-amber-700" data-testid={`kalk-rundung-${attId}`}> (Stückpreis gerundet: {rundung > 0 ? "+" : "−"}{fmtEur(Math.abs(rundung))} ggü. Kalkulation {fmtEur(ang.kalkuliert_brutto)})</span>}
        </span>
      )}
    </span>
  );
}
